import { jwtDecode } from 'jwt-decode';
import { SessionContext } from '../types/sessionContext';
import {
  browserSupportsWebAuthn,
  RegistrationResponseJSON,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import type {
  Authenticated,
  AuthResult,
  Challenge,
  GoogleLoginRequest,
} from '@ecosconnect/core/src/auth/types';
import {
  createAuthClient,
  parseAuthResponse,
} from '@ecosconnect/core/src/auth/client';

const apiBase = import.meta.env.ECOS_API_BASE;
const HAS_PASSKEY_AUTH_KEY = 'has-passkey-auth';
export const TWO_FACTOR_PROMPT_KEY = 'two-factor-prompt';
export const TWO_FACTOR_PROMPT_DISMISSED_AT_KEY =
  'two-factor-prompt-dismissed-at';

export const storeAdminToken = (token: string): void => {
  localStorage.setItem('admin-token', token);
};

export const storeToken = (token: string): void => {
  localStorage.setItem('auth-token', token);
};

export const hasToken = (): boolean => {
  return getToken() !== null;
};

export const getAdminToken = (): string | null => {
  return localStorage.getItem('admin-token');
};

export const getToken = (): string | null => {
  return localStorage.getItem('auth-token');
};

export const getTokenData = (): SessionContext => {
  const token = getToken();
  if (!token) {
    throw new Error('No token data available');
  }
  return jwtDecode(token);
};

const AUTH_PATHS = [
  '/login',
  '/logout',
  '/forgot-password',
  '/password-reset',
  '/token',
];

const getLoginRedirectPath = () => {
  const redirect = new URLSearchParams(window.location.search).get('redirect');

  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return null;
  }

  const redirectPath = redirect.split(/[?#]/)[0];

  if (
    AUTH_PATHS.some(
      (authPath) =>
        redirectPath === authPath || redirectPath.startsWith(`${authPath}/`)
    )
  ) {
    return null;
  }

  return redirect;
};

export type LoginResult =
  | { kind: 'authenticated' }
  | { kind: 'challenge'; challenge: Challenge };

const authenticated = (result: AuthResult): Authenticated => {
  if (result.kind !== 'authenticated') {
    throw new Error('Unexpected response from the server');
  }
  return result;
};

const readAuthenticated = async (res: Response): Promise<Authenticated> =>
  authenticated(
    parseAuthResponse(res.status, await res.json().catch(() => ({})))
  );

const getPasskeyAuthStorageKey = (token: string): string => {
  const sessionContext = jwtDecode<SessionContext>(token);
  return `${HAS_PASSKEY_AUTH_KEY}:${apiBase}:${sessionContext.profile.id}`;
};

const getHasPasskeyAuth = (token: string): boolean => {
  return JSON.parse(
    localStorage.getItem(getPasskeyAuthStorageKey(token)) || 'false'
  );
};

const setHasPasskeyAuth = (token: string): void => {
  localStorage.setItem(getPasskeyAuthStorageKey(token), 'true');
};

export const goToHomePage = () => {
  const redirectPath = getLoginRedirectPath();

  if (redirectPath) {
    window.location.replace(redirectPath);
    return;
  }

  const sessionContext = getTokenData();

  if (!sessionContext.modes) {
    console.warn('No modes found in token data', sessionContext);
    logout();
    return;
  }

  if (sessionContext.modes.includes('ADMIN')) {
    window.location.replace('/admin/users');
  } else if (sessionContext.modes.includes('WATER_SUPPLIER')) {
    window.location.replace('/water-supplier/');
  } else if (sessionContext.modes.includes('COMPANY')) {
    window.location.replace('/company/jobs');
  } else if (
    sessionContext.modes.includes('INDIVIDUAL') &&
    window.location.pathname !== '/individual/jobs'
  ) {
    window.location.replace('/individual/jobs');
  }
};

type SignInOptions = {
  isPasswordLogin?: boolean;
  promptForPasskeyRegistration?: boolean;
  hasPasskeyAuth?: boolean;
};

export const signIn = async (
  result: Authenticated,
  options: SignInOptions = {}
): Promise<void> => {
  const existingToken = getToken();
  if (existingToken) {
    storeAdminToken(existingToken);
  }

  storeToken(result.token);

  const dismissalAge =
    Date.now() -
    Date.parse(localStorage.getItem(TWO_FACTOR_PROMPT_DISMISSED_AT_KEY) ?? '');
  const recentlyDismissed =
    dismissalAge >= 0 && dismissalAge < 30 * 24 * 60 * 60 * 1000;

  if (options.isPasswordLogin && !recentlyDismissed) {
    sessionStorage.setItem(TWO_FACTOR_PROMPT_KEY, 'true');
  } else {
    sessionStorage.removeItem(TWO_FACTOR_PROMPT_KEY);
  }

  if (options.hasPasskeyAuth) {
    setHasPasskeyAuth(result.token);
  }

  if (
    browserSupportsWebAuthn() &&
    (options.promptForPasskeyRegistration ?? true) &&
    !getHasPasskeyAuth(result.token)
  ) {
    try {
      await registerPasskey();
    } catch (err) {
      console.error(err);
    }
  }

  goToHomePage();
};

const postJson = (path: string, body: object, token?: string | null) =>
  fetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

export const authClient = createAuthClient({
  post: async (path, body, { auth }) => {
    const token = auth ? getToken() : null;
    if (auth && !token) throw new Error('Not logged in');
    const res = await postJson(path, body, token);
    return { status: res.status, body: await res.json().catch(() => ({})) };
  },
});

const signInOrChallenge = async (
  result: AuthResult,
  options?: SignInOptions
): Promise<LoginResult> => {
  if (result.kind === 'challenge') return result;
  await signIn(result, options);
  return { kind: 'authenticated' };
};

export const login = async (
  email: string,
  password: string
): Promise<LoginResult> =>
  signInOrChallenge(await authClient.login({ email, password }), {
    isPasswordLogin: true,
  });

export const loginWithGoogle = async (
  credential: GoogleLoginRequest
): Promise<LoginResult> =>
  signInOrChallenge(await authClient.loginWithGoogle(credential));

export const registerPasskey = async (
  silent: boolean = false,
  password?: string
) => {
  const token = getToken();
  if (!token) {
    throw new Error('Not logged in');
  }

  WebAuthnAbortService.cancelCeremony();

  const registrationOptionsResponse = await fetch(
    `${apiBase}/auth/webauthn/registration-options`,
    password === undefined
      ? { headers: { authorization: `Bearer ${token}` } }
      : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ password }),
        }
  );
  const registrationOptions = await registrationOptionsResponse.json();
  if (!registrationOptionsResponse.ok) {
    throw new Error(
      registrationOptions.message || 'Unable to start passkey registration'
    );
  }

  const { optionsJSON, challengeToken } = registrationOptions;

  const attResp = await startRegistration({
    optionsJSON,
    useAutoRegister: silent,
  });

  const verificationResp = await fetch(
    `${apiBase}/auth/webauthn/verify-registration`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({
        response: attResp,
        challengeToken,
      }),
    }
  );

  const verificationJSON = await verificationResp.json();
  if (!verificationResp.ok) {
    throw new Error(
      verificationJSON.message || 'Failed to verify passkey registration'
    );
  }

  setHasPasskeyAuth(token);
  return verificationJSON;
};

export const authenticateWithPasskey = async (
  autofill: boolean
): Promise<void> => {
  WebAuthnAbortService.cancelCeremony();

  const resp = await fetch(`${apiBase}/auth/webauthn/authentication-options`, {
    method: 'GET',
  });
  const { optionsJSON, challengeToken } = await resp.json();

  const asseResp = await startAuthentication({
    optionsJSON,
    useBrowserAutofill: autofill,
  });

  const result = await authClient.loginWithPasskey({
    response: asseResp,
    challengeToken,
  });

  await signIn(authenticated(result), {
    promptForPasskeyRegistration: false,
    hasPasskeyAuth: true,
  });
};

export const impersonate = (userId: string | number): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Not logged in');
  }

  return fetch(`${apiBase}/impersonate?userId=${encodeURIComponent(userId)}`, {
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  }).then(async (res) => {
    await signIn(await readAuthenticated(res), {
      promptForPasskeyRegistration: false,
    });
  });
};

export const isImpersonating = () => !!getAdminToken();

export const logout = (): void => {
  localStorage.removeItem('auth-token');
  sessionStorage.removeItem(TWO_FACTOR_PROMPT_KEY);
  const adminToken = getAdminToken();
  if (adminToken) {
    storeToken(adminToken);
    localStorage.removeItem('admin-token');
    window.location.href = '/admin/users';
    return;
  }

  window.location.href = '/login';
};

const forgotPasswordResHandler = async (res: Response) => {
  const responseJson = await res.json();
  if (res.status >= 400) {
    if (responseJson.message) {
      throw new Error(responseJson.message);
    }
  }
  const { errors } = responseJson;
  if (errors?.length > 0) {
    throw new Error(errors[0].message);
  }
  return;
};

export const sendForgotPasswordEmail = (email: string): Promise<void> => {
  return fetch(`${apiBase}/password-reset-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  }).then(forgotPasswordResHandler);
};

export const sendForgottenPasswordUpdate = (payload: object): Promise<void> => {
  return fetch(`${apiBase}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }).then(forgotPasswordResHandler);
};
